import React, { useEffect } from 'react';
import { CheckboxField, CheckboxGroup } from '@flos/react-ui';
import useAppState from '../../state/appState';
import { Checkboxes as CheckboxesType, FormValues, FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { ElementValue } from '../../types/form-values';
import { ClaimFormSubmitFieldValue } from '../../types/claim-form-submit-field-value';
import { getIsValid, getValidationErrors } from '../../validators/validator-mapping';
import { SummaryRowTemplate } from '../../helpers/templates';
import { ElementValueHuman } from '../../types/element-values';
import { getValue } from '../values/Get';
import { getElementFromElementId } from '../../helpers/getElementFromElementId';
import { getClaimFormSubmitValue } from '../../helpers/getClaimFormSubmitValue';

const Checkboxes = ({ values, helpText, elementId, caption, description }: CheckboxesType) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const setFormValue = useAppState((state) => state.setFormValue);

    const value = Checkboxes.getValueOrDefault(formValues, elementId);

    const [errorMessage, setErrorMessage] = React.useState('');

    const [checkedBoxes, setCheckedBoxes] = React.useState<{ label: string; value: string }[]>(value);

    const [fieldHasChangedAtLeastOnce, setFieldHasChangedAtLeastOnce] = React.useState(false);

    const checkedHandler = (evt: React.ChangeEvent<HTMLInputElement>, label: string) => {
        if (!formConfig) {
            return;
        }

        setFieldHasChangedAtLeastOnce(true);

        const { value, checked: isChecked } = evt.target;

        let listToUpdate = checkedBoxes;
        if (isChecked) {
            if (!listToUpdate.some((e) => e.value === value)) {
                listToUpdate = [...listToUpdate, { label, value }];
            }
        } else {
            listToUpdate = listToUpdate.filter((c) => c.value !== value);
        }
        setCheckedBoxes(listToUpdate);

        const checkedList = listToUpdate.filter((v) => v.value !== '' && v.value !== undefined);
        setFormValue(elementId, checkedList);
    };

    const doValidation = () => {
        if (!formConfig) {
            return;
        }

        if (!fieldHasChangedAtLeastOnce) {
            return;
        }

        const errorMessages = getValidationErrors(formConfig, formValues, elementId);
        setErrorMessage(errorMessages[0]);
    };

    useEffect(() => {
        doValidation();
        // eslint-disable-next-line
    }, [value.join(', ')]);

    const isValid = Checkboxes.isValidField(formConfig, formValues, elementId, fieldHasChangedAtLeastOnce);

    return (
        <CheckboxGroup
            errorText={errorMessage}
            id={elementId}
            key={elementId}
            helpText={helpText}
            isValid={isValid}
            description={description}
        >
            {caption && <h6>{caption}</h6>}
            {values.map((option) => (
                <CheckboxField
                    key={'checkbox' + option.value}
                    label={option.caption}
                    value={option.value}
                    onChange={(e) => {
                        checkedHandler(e, option.caption);
                    }}
                    checked={checkedBoxes.some((e) => e.value === option.value)}
                    text={option.text}
                />
            ))}
        </CheckboxGroup>
    );
};

Checkboxes.ResumePage = (formConfig: FormConfig, formValues: FormValues, elementId: string): JSX.Element => {
    const element = getElementFromElementId(formConfig, elementId) as CheckboxesType;
    const { values, summaryCaption } = element;

    const value = getValue(formConfig, formValues, elementId);

    // Happens when the user has not filled
    // out previous pages but somehow navigated to the resume page
    if (value === undefined || value.length < 1) {
        return <></>;
    }

    const listOfCheckedValues = value.map((option: { label: string; value: string }, i: number) => {
        const found = values.find((val) => val.value === option.value);
        // @YIJ
        //   - Why not skip this if it is not found?
        return <p key={'checkboxes-' + elementId + '-' + i}>{found?.caption}</p>;
    });

    return <SummaryRowTemplate elementId={elementId} caption={summaryCaption} value={listOfCheckedValues} />;
};

Checkboxes.getValueSubmit = (formValues: FormValues, elementId: string, label: string): ClaimFormSubmitFieldValue => {
    const valueObj = Checkboxes.getValue(formValues, elementId)
        .map((e: any) => e.value)
        .join(', ');

    const valueStr = Checkboxes.getValueHuman(formValues, elementId);
    const valueId = { string: valueObj };

    return getClaimFormSubmitValue('checkboxes', elementId, label, valueStr, valueId);
};

Checkboxes.getValue = (formValues: FormValues, elementId: string): ElementValue => {
    if (formValues[elementId] === undefined) {
        return [];
    }
    return formValues[elementId];
};

Checkboxes.getValueOrDefault = (formValues: FormValues, elementId: string) => {
    const value = Checkboxes.getValue(formValues, elementId);
    if (value === undefined) {
        return [];
    }
    return value;
};

Checkboxes.getValueHuman = (formValues: FormValues, elementId: string): ElementValueHuman => {
    const valueObj = Checkboxes.getValue(formValues, elementId);
    const valueStr = valueObj.map((e: any) => e.label).join(', ');

    return valueStr;
};

Checkboxes.isValid = (formConfig: FormConfig, formValues: FormValues, elementId: string): boolean => {
    const isValid = getIsValid(formConfig, formValues, elementId);
    return isValid;
};

Checkboxes.isValidField = (
    formConfig: FormConfig | undefined,
    formValues: FormValues,
    elementId: string,
    fieldHasChangedAtLeastOnce: boolean
): boolean | undefined => {
    if (!formConfig) {
        return undefined;
    }

    if (!fieldHasChangedAtLeastOnce) {
        return undefined;
    }

    const value = Checkboxes.getValue(formValues, elementId);

    // When the user has not typed anything,
    // undefined is returned to prevent
    // showing red errors all over as soon
    // as he sees the form
    if (value === '' || value === undefined) {
        return undefined;
    }

    const isValid = Checkboxes.isValid(formConfig, formValues, elementId);

    return isValid;
};

export default Checkboxes;
